import React, { forwardRef, memo } from 'react';
import Avatar from '../../atoms/avatar/Avatar';
import DropdownMenu from '../../molecules/dropdown-menu/DropdownMenu';
import { cva, type VariantProps } from 'class-variance-authority';
import cn from '../../utils/cn';
import IconBookmarkPin from '../../icons/IconBookmarkPin.tsx';
import IconVerticalDots from '../../icons/IconVerticalDots.tsx';
import ButtonCTA, {
  ButtonCTASize,
  ButtonCTAVariant,
} from '../../atoms/buttons/button-cta/ButtonCTA.tsx';
import IconArrowRight from '../../icons/IconArrowRight.tsx';
import ErrorStateCard from '../../atoms/cards/error-state-card/ErrorStateCard.tsx';
import Button, { ButtonSize, ButtonVariant } from '../../atoms/buttons/button/Button.tsx';
import ErrorBoundary from '../../molecules/error-boundary/ErrorBoundary.tsx';
import IconLbiBrandGradient from '../../icons/IconLbiBrandGradient.tsx';

export enum ActivityCardRootVariant {
  DEFAULT,
  INTERKAB,
}

type ActivityCardRootProps = {
  variant?: ActivityCardRootVariant;
} & React.ComponentPropsWithoutRef<'div'>;

const ActivityCardRoot = React.forwardRef<React.ElementRef<'div'>, ActivityCardRootProps>(
  ({ children, className, variant = ActivityCardRootVariant.DEFAULT, ...props }, ref) => {
    return (
      <div
        role="article"
        ref={ref}
        className={cn(
          'relative overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-neutral-300',
          className,
        )}
        {...props}
      >
        {variant === ActivityCardRootVariant.INTERKAB ? (
          <div className={cn('pointer-events-none absolute -top-10 right-16 size-20')}>
            <IconLbiBrandGradient className="h-48" />
          </div>
        ) : null}

        <div className="relative z-10 flex flex-col gap-4 p-3">{children}</div>
      </div>
    );
  },
);

ActivityCardRoot.displayName = 'ActivityCard.Root';

type ActivityCardHeaderProps = React.ComponentPropsWithoutRef<'div'>;

const ActivityCardHeader = ({
  children,
  className,
  ...props
}: ActivityCardHeaderProps) => {
  return (
    <div className={cn('flex items-center justify-between gap-3', className)} {...props}>
      {children}
    </div>
  );
};

ActivityCardHeader.displayName = 'ActivityCard.Header';

type ActivityCardHeaderUserProps = React.ComponentPropsWithoutRef<'div'>;

const ActivityCardHeaderUser = ({
  children,
  className,
  ...props
}: ActivityCardHeaderUserProps) => {
  return (
    <div className={cn('flex items-center gap-2.5', className)} {...props}>
      {children}
    </div>
  );
};

ActivityCardHeaderUser.displayName = 'ActivityCard.Header.User';

type ActivityCardHeaderUserAvatarProps = React.ComponentPropsWithoutRef<typeof Avatar>;

const ActivityCardHeaderUserAvatar = ({
  className,
  ...props
}: ActivityCardHeaderUserAvatarProps) => {
  return <Avatar className={cn('size-9', className)} {...props} />;
};

ActivityCardHeaderUserAvatar.displayName = 'ActivityCard.Header.User.Avatar';

type ActivityCardHeaderUserInformationsProps = React.ComponentPropsWithoutRef<'div'>;

const ActivityCardHeaderUserInformations = ({
  children,
  className,
  ...props
}: ActivityCardHeaderUserInformationsProps) => {
  return (
    <div
      className={cn('flex flex-col items-start justify-center gap-0.5', className)}
      {...props}
    >
      {children}
    </div>
  );
};

ActivityCardHeaderUserInformations.displayName = 'ActivityCard.Header.User.Informations';

type ActivityCardHeaderUserInformationsNameProps = React.ComponentPropsWithoutRef<'span'>;

const ActivityCardHeaderUserInformationsName = ({
  children,
  className,
  ...props
}: ActivityCardHeaderUserInformationsNameProps) => {
  return (
    <span className={cn('text-xs font-semibold text-neutral-700', className)} {...props}>
      {children}
    </span>
  );
};

ActivityCardHeaderUserInformationsName.displayName =
  'ActivityCard.Header.User.Informations.Name';

type ActivityCardHeaderUserInformationsDescriptionProps =
  React.ComponentPropsWithoutRef<'span'>;

const ActivityCardHeaderUserInformationsDescription = ({
  children,
  className,
  ...props
}: ActivityCardHeaderUserInformationsDescriptionProps) => {
  return (
    <span
      className={cn('break-words text-2xs font-normal text-neutral-400', className)}
      {...props}
    >
      {children}
    </span>
  );
};

ActivityCardHeaderUserInformationsDescription.displayName =
  'ActivityCard.Header.User.Informations.Description';

type ActivityCardHeaderMetadataProps = React.ComponentPropsWithoutRef<'div'>;

const ActivityCardHeaderMetadata = ({
  children,
  className,
  ...props
}: ActivityCardHeaderMetadataProps) => {
  return (
    <div
      className={cn(
        'flex flex-1 flex-wrap-reverse items-center justify-end gap-2.5',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
};

ActivityCardHeaderMetadata.displayName = 'ActivityCard.Header.Metadata';

type ActivityCardHeaderMetadataDateProps = React.ComponentPropsWithoutRef<'span'>;

const ActivityCardHeaderMetadataDate = ({
  children,
  className,
  ...props
}: ActivityCardHeaderMetadataDateProps) => {
  return (
    <span
      className={cn('text-right text-2xs font-normal text-neutral-400', className)}
      {...props}
    >
      {children}
    </span>
  );
};

ActivityCardHeaderMetadataDate.displayName = 'ActivityCard.Header.Metadata.Date';

type ActivityCardHeaderMetadataPinnedProps = React.ComponentPropsWithoutRef<'svg'>;

const ActivityCardHeaderMetadataPinned = (
  props: ActivityCardHeaderMetadataPinnedProps,
) => {
  return <IconBookmarkPin className="text-primary size-5" {...props} />;
};

ActivityCardHeaderMetadataPinned.displayName = 'ActivityCard.Header.Metadata.Pinned';

export enum ActivityCardHeaderMetadataReadIndicatorVariant {
  UNREAD,
  READ,
}

const activityCardHeaderMetadataReadIndicatorVariants = cva(
  'size-2.5 rounded-full bg-primary transition-colors',
  {
    variants: {
      variant: {
        [ActivityCardHeaderMetadataReadIndicatorVariant.UNREAD]:
          'bg-brand-500 group-hover/read-indicator:bg-brand-600 group-focus-visible/read-indicator:bg-brand-600',
        [ActivityCardHeaderMetadataReadIndicatorVariant.READ]:
          'bg-neutral-100 ring-1 ring-inset ring-neutral-300 group-hover/read-indicator:bg-neutral-300 group-focus-visible/read-indicator:bg-neutral-300',
      } satisfies Record<ActivityCardHeaderMetadataReadIndicatorVariant, string>,
    },
  },
);

type ActivityCardHeaderMetadataReadIndicatorProps = VariantProps<
  typeof activityCardHeaderMetadataReadIndicatorVariants
> &
  React.ComponentPropsWithoutRef<'button'>;

const ActivityCardHeaderMetadataReadIndicator = ({
  className,
  variant,
  ...props
}: ActivityCardHeaderMetadataReadIndicatorProps) => {
  return (
    <button
      className={cn('group/read-indicator p-1', className)}
      aria-label={
        variant === ActivityCardHeaderMetadataReadIndicatorVariant.UNREAD
          ? 'Marquer comme lu'
          : 'Marquer comme non lu'
      }
      {...props}
    >
      <div className={cn(activityCardHeaderMetadataReadIndicatorVariants({ variant }))} />
    </button>
  );
};

ActivityCardHeaderMetadataReadIndicator.displayName =
  'ActivityCard.Header.Metadata.ReadIndicator';

type ActivityCardHeaderMetadataDropdownProps = React.ComponentPropsWithoutRef<
  typeof DropdownMenu.Root
>;

const ActivityCardHeaderMetadataDropdown = ({
  children,
  ...props
}: ActivityCardHeaderMetadataDropdownProps) => {
  return (
    <DropdownMenu.Root {...props}>
      <DropdownMenu.Trigger className="rounded-md p-1 transition-colors hover:bg-neutral-200 focus-visible:bg-neutral-200 active:bg-neutral-200 data-[state=open]:bg-neutral-200">
        <IconVerticalDots className="h-3" />
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="end" className="z-1050">
        <DropdownMenu.Group>{children}</DropdownMenu.Group>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
};

ActivityCardHeaderMetadataDropdown.displayName = 'ActivityCard.Header.Metadata.Dropdown';

type ActivityCardHeaderMetadataDropdownItemProps = React.ComponentPropsWithoutRef<
  typeof DropdownMenu.Item
>;

const ActivityCardHeaderMetadataDropdownItem = ({
  ...props
}: ActivityCardHeaderMetadataDropdownItemProps) => {
  return <DropdownMenu.Item {...props} />;
};

ActivityCardHeaderMetadataDropdownItem.displayName =
  'ActivityCard.Header.Metadata.Dropdown.Item';

export enum ActivityCardContentVariant {
  DEFAULT,
  BORDERLESS,
}

type ActivityCardContentProps = React.ComponentPropsWithoutRef<'div'> & {
  variant?: ActivityCardContentVariant;
};

const ActivityCardContent = ({
  variant = ActivityCardContentVariant.DEFAULT,
  children,
  className,
  ...props
}: ActivityCardContentProps) => {
  return (
    <div
      className={cn(
        'flex flex-col gap-1 rounded-xl bg-white p-4 ring-1 ring-inset ring-neutral-200',
        {
          'rounded-none bg-transparent p-0 ring-0':
            variant === ActivityCardContentVariant.BORDERLESS,
        },
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
};

ActivityCardContent.displayName = 'ActivityCard.Content';

const ActivityCardContentButton = memo(
  forwardRef<
    React.ComponentRef<typeof ButtonCTA>,
    Omit<
      React.ComponentPropsWithoutRef<typeof ButtonCTA>,
      'variant' | 'size' | 'iconRight' | 'iconLeft'
    >
  >(({ className, children, asChild, ...props }, ref) => (
    <ButtonCTA
      ref={ref}
      variant={ButtonCTAVariant.DARK_GRAY_BRAND_OUTLINE}
      size={ButtonCTASize.SMALL}
      className={cn('w-fit shrink-0', className)}
      asChild={asChild}
      {...props}
    >
      {asChild ? (
        children
      ) : (
        <>
          <IconArrowRight />
          {children}
        </>
      )}
    </ButtonCTA>
  )),
);

ActivityCardContentButton.displayName = 'ActivityCard.Content.Button';

const ActivityCardContentEmptyCard = memo(
  ({ ...props }: React.ComponentPropsWithoutRef<typeof ErrorStateCard>) => {
    return (
      <ErrorStateCard
        i18n={{ message: "Cette information n'est plus disponible" }}
        {...props}
      />
    );
  },
);

ActivityCardContentEmptyCard.displayName = 'ActivityCard.Content.EmptyCard';

type ActivityCardSocialProps = React.ComponentPropsWithoutRef<'div'>;

const ActivityCardSocial = ({
  children,
  className,
  ...props
}: ActivityCardSocialProps) => {
  return (
    <div
      className={cn(
        'flex flex-col gap-2.5 border-t border-t-neutral-100 pt-2.5',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
};

ActivityCardSocial.displayName = 'ActivityCard.Social';

type ActivityCardSocialPreviewProps = React.ComponentPropsWithoutRef<'div'>;

const ActivityCardSocialPreview = ({
  children,
  className,
  ...props
}: ActivityCardSocialPreviewProps) => {
  return (
    <div
      className={cn(
        'flex items-center justify-between border-b border-b-neutral-100 pb-2.5',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
};

ActivityCardSocialPreview.displayName = 'ActivityCard.Social.Preview';

type ActivityCardSocialPreviewReactionsProps = React.ComponentPropsWithoutRef<'div'>;

const ActivityCardSocialPreviewReactions = ({
  children,
  className,
  ...props
}: ActivityCardSocialPreviewReactionsProps) => {
  return (
    <div className={cn('flex min-h-7 items-center gap-1.5', className)} {...props}>
      {children}
    </div>
  );
};

ActivityCardSocialPreviewReactions.displayName = 'ActivityCard.Social.Preview.Reactions';

type ActivityCardSocialPreviewReactionsStackProps = React.ComponentPropsWithoutRef<'div'>;

const ActivityCardSocialPreviewReactionsStack = ({
  children,
  className,
  ...props
}: ActivityCardSocialPreviewReactionsStackProps) => {
  return (
    <div className={cn('flex -space-x-1', className)} {...props}>
      {children}
    </div>
  );
};

ActivityCardSocialPreviewReactionsStack.displayName =
  'ActivityCard.Social.Preview.Reactions.Stack';

type ActivityCardSocialPreviewReactionsItemProps = React.ComponentPropsWithoutRef<'div'>;

const ActivityCardSocialPreviewReactionsItem = ({
  children,
  className,
  ...props
}: ActivityCardSocialPreviewReactionsItemProps) => {
  return (
    <div
      className={cn(
        'ring-offset-primary/50 flex h-7 w-7 items-center justify-center rounded-full bg-white text-center shadow-md ring-2 ring-white ring-offset-1 animate-in fade-in-0',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
};

ActivityCardSocialPreviewReactionsItem.displayName =
  'ActivityCard.Social.Preview.Reactions.Item';

type ActivityCardSocialPreviewCommentsCountProps =
  React.ComponentPropsWithoutRef<'button'>;

const ActivityCardSocialPreviewCommentsCount = ({
  children,
  className,
  ...props
}: ActivityCardSocialPreviewCommentsCountProps) => {
  return (
    <button
      className={cn(
        'text-xs font-normal text-neutral-700 hover:underline disabled:pointer-events-none',
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
};

ActivityCardSocialPreviewCommentsCount.displayName =
  'ActivityCard.Social.Preview.CommentsCount';

type ActivityCardSocialPreviewReactionsCountProps =
  React.ComponentPropsWithoutRef<'span'>;

const ActivityCardSocialPreviewReactionsCount = ({
  children,
  className,
  ...props
}: ActivityCardSocialPreviewReactionsCountProps) => {
  return (
    <span className={cn('text-xs font-normal text-neutral-700', className)} {...props}>
      {children}
    </span>
  );
};

ActivityCardSocialPreviewReactionsCount.displayName =
  'ActivityCard.Social.Preview.Reactions.Count';

type ActivityCardSocialCommentsProps = React.ComponentPropsWithoutRef<'div'>;

const ActivityCardSocialComments = ({
  children,
  className,
  ...props
}: ActivityCardSocialCommentsProps) => {
  return (
    <div
      className={cn(
        'flex flex-col gap-4 border-b border-b-neutral-100 pb-2.5',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
};

ActivityCardSocialComments.displayName = 'ActivityCard.Social.Comments';

type ActivityCardSocialCommentsItemProps = React.ComponentPropsWithoutRef<'div'>;

const ActivityCardSocialCommentsItem = ({
  children,
  className,
  ...props
}: ActivityCardSocialCommentsItemProps) => {
  return (
    <div
      className={cn('group/comments-item flex items-start gap-2.5', className)}
      {...props}
    >
      {children}
    </div>
  );
};

ActivityCardSocialCommentsItem.displayName = 'ActivityCard.Social.Comments.Item';

type ActivityCardSocialCommentsItemAvatarProps = React.ComponentPropsWithoutRef<
  typeof Avatar
>;

const ActivityCardSocialCommentsItemAvatar = ({
  className,
  ...props
}: ActivityCardSocialCommentsItemAvatarProps) => {
  return <Avatar className={cn('size-9', className)} {...props} />;
};

ActivityCardSocialCommentsItemAvatar.displayName =
  'ActivityCard.Social.Comments.Item.Avatar';

type ActivityCardSocialCommentsItemContentProps = React.ComponentPropsWithoutRef<'div'>;

const ActivityCardSocialCommentsItemContent = ({
  children,
  className,
  ...props
}: ActivityCardSocialCommentsItemContentProps) => {
  return (
    <div
      className={cn(
        'flex flex-1 flex-col gap-1.5 border-b border-b-neutral-100 pb-4 group-last/comments-item:border-b-0 group-last/comments-item:pb-0',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
};

ActivityCardSocialCommentsItemContent.displayName =
  'ActivityCard.Social.Comments.Item.Content';

type ActivityCardSocialCommentsItemContentHeaderProps =
  React.ComponentPropsWithoutRef<'div'>;

const ActivityCardSocialCommentsItemContentHeader = ({
  children,
  className,
  ...props
}: ActivityCardSocialCommentsItemContentHeaderProps) => {
  return (
    <div
      className={cn('flex items-center justify-between gap-2.5', className)}
      {...props}
    >
      {children}
    </div>
  );
};

ActivityCardSocialCommentsItemContentHeader.displayName =
  'ActivityCard.Social.Comments.Item.Content.Header';

type ActivityCardSocialCommentsItemContentHeaderNameProps =
  React.ComponentPropsWithoutRef<'span'>;

const ActivityCardSocialCommentsItemContentHeaderName = ({
  children,
  className,
  ...props
}: ActivityCardSocialCommentsItemContentHeaderNameProps) => {
  return (
    <span
      className={cn(
        'line-clamp-2 max-w-[15ch] break-words text-xs font-semibold text-neutral-800 2xl:max-w-[30ch]',
        className,
      )}
      {...props}
    >
      {children}
    </span>
  );
};

ActivityCardSocialCommentsItemContentHeaderName.displayName =
  'ActivityCard.Social.Comments.Item.Content.Header.Name';

type ActivityCardSocialCommentsItemContentHeaderDateProps =
  React.ComponentPropsWithoutRef<'span'>;

const ActivityCardSocialCommentsItemContentHeaderDate = ({
  children,
  className,
  ...props
}: ActivityCardSocialCommentsItemContentHeaderDateProps) => {
  return (
    <span
      className={cn('text-right text-2xs font-normal text-neutral-500', className)}
      {...props}
    >
      {children}
    </span>
  );
};

ActivityCardSocialCommentsItemContentHeaderDate.displayName =
  'ActivityCard.Social.Comments.Item.Content.Header.Date';

type ActivityCardSocialCommentsItemContentTextProps =
  React.ComponentPropsWithoutRef<'div'>;

const ActivityCardSocialCommentsItemContentText = ({
  children,
  className,
  ...props
}: ActivityCardSocialCommentsItemContentTextProps) => {
  return (
    <div
      className={cn(
        'line-clamp-2 whitespace-pre-wrap text-xs font-normal text-neutral-700',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
};

ActivityCardSocialCommentsItemContentText.displayName =
  'ActivityCard.Social.Comments.Item.Content.Text';

type ActivityCardSocialReactProps = React.ComponentPropsWithoutRef<'div'>;

const ActivityCardSocialReact = ({
  children,
  className,
  ...props
}: ActivityCardSocialReactProps) => {
  return (
    <div className={cn('flex items-center gap-2.5', className)} {...props}>
      {children}
    </div>
  );
};

ActivityCardSocialReact.displayName = 'ActivityCard.Social.React';

export enum ActivityCardSocialReactVariant {
  ACTIVE,
}

const activityCardSocialReactStyles = cva(
  'w-full text-sm text-neutral-700 shadow-none ring-0 hover:bg-primary/5 hover:ring-0 focus:ring-0',
  {
    variants: {
      variant: {
        [ActivityCardSocialReactVariant.ACTIVE]:
          'bg-primary/10 hover:bg-primary/20 font-medium',
      },
    },
  },
);

type ActivityCardSocialReactLikeButtonProps = Omit<
  React.ComponentPropsWithoutRef<typeof Button>,
  'variant' | 'size'
> &
  VariantProps<typeof activityCardSocialReactStyles>;

const ActivityCardSocialReactLikeButton = forwardRef<
  React.ComponentRef<typeof Button>,
  ActivityCardSocialReactLikeButtonProps
>(({ className, variant, ...props }, ref) => {
  return (
    <Button
      ref={ref}
      variant={ButtonVariant.DEFAULT}
      size={ButtonSize.SM}
      className={cn(activityCardSocialReactStyles({ variant }), className)}
      {...props}
    />
  );
});

ActivityCardSocialReactLikeButton.displayName = 'ActivityCard.Social.React.LikeButton';

type ActivityCardSocialReactCommentButtonProps = Omit<
  React.ComponentPropsWithoutRef<typeof Button>,
  'variant' | 'size'
>;

const ActivityCardSocialReactCommentButton = forwardRef<
  React.ComponentRef<typeof Button>,
  ActivityCardSocialReactCommentButtonProps
>(({ className, ...props }, ref) => {
  return (
    <Button
      ref={ref}
      variant={ButtonVariant.DEFAULT}
      size={ButtonSize.SM}
      className={cn(
        'hover:bg-primary/5 data-[state=open]:bg-primary/10 data-[state=open]:hover:bg-primary/20 w-full text-sm text-neutral-700 shadow-none ring-0 hover:ring-0 focus:ring-0 data-[state=open]:font-medium [&_svg]:size-4',
        className,
      )}
      {...props}
    />
  );
});

ActivityCardSocialReactCommentButton.displayName =
  'ActivityCard.Social.React.CommentButton';

export const ActivityCard = {
  Root: ActivityCardRoot,
  Header: Object.assign(ActivityCardHeader, {
    User: Object.assign(ActivityCardHeaderUser, {
      Avatar: ActivityCardHeaderUserAvatar,
      Informations: Object.assign(ActivityCardHeaderUserInformations, {
        Name: ActivityCardHeaderUserInformationsName,
        Description: ActivityCardHeaderUserInformationsDescription,
      }),
    }),
    Metadata: Object.assign(ActivityCardHeaderMetadata, {
      Pinned: ActivityCardHeaderMetadataPinned,
      ReadIndicator: ActivityCardHeaderMetadataReadIndicator,
      Date: ActivityCardHeaderMetadataDate,
      Dropdown: Object.assign(ActivityCardHeaderMetadataDropdown, {
        Item: ActivityCardHeaderMetadataDropdownItem,
      }),
    }),
  }),
  Content: Object.assign(ActivityCardContent, {
    Button: ActivityCardContentButton,
    EmptyCard: ActivityCardContentEmptyCard,
    ErrorBoundary,
  }),
  Social: Object.assign(ActivityCardSocial, {
    Preview: Object.assign(ActivityCardSocialPreview, {
      Reactions: Object.assign(ActivityCardSocialPreviewReactions, {
        Stack: ActivityCardSocialPreviewReactionsStack,
        Item: ActivityCardSocialPreviewReactionsItem,
        Count: ActivityCardSocialPreviewReactionsCount,
      }),
      CommentsCount: ActivityCardSocialPreviewCommentsCount,
    }),
    Comments: Object.assign(ActivityCardSocialComments, {
      Item: Object.assign(ActivityCardSocialCommentsItem, {
        Avatar: ActivityCardSocialCommentsItemAvatar,
        Content: Object.assign(ActivityCardSocialCommentsItemContent, {
          Header: Object.assign(ActivityCardSocialCommentsItemContentHeader, {
            Name: ActivityCardSocialCommentsItemContentHeaderName,
            Date: ActivityCardSocialCommentsItemContentHeaderDate,
          }),
          Text: ActivityCardSocialCommentsItemContentText,
        }),
      }),
    }),
    React: Object.assign(ActivityCardSocialReact, {
      LikeButton: ActivityCardSocialReactLikeButton,
      CommentButton: ActivityCardSocialReactCommentButton,
    }),
  }),
} as const;
